import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import {
  Page,
  Card,
  EmptyState,
  BlockStack,
  Text,
  InlineStack,
  Badge,
  Box,
  Button as PolarisButton,
  Spinner,
} from "@shopify/polaris";
import { getTemplateById } from "../../data/calendarWidgetTemplates";
import { deleteWidgetFromDb } from "../../helper/widgetApi";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { useWidgetsFromSession } from "../../hooks/useAppSessionData";
import { useBilling } from "../../context/BillingContext";
import { pickPublishedWidgetId } from "../../utils/planAccess";
import PlanLockOverlay from "../../components/PlanLockOverlay";

export default function WidgetsListPage() {
  const location = useLocation();
  const navigate = useAppNavigate();
  const { widgets, setWidgets, loading } = useWidgetsFromSession();
  const billing = useBilling();
  const publishedWidgetId = billing.isFreePlan ? pickPublishedWidgetId(widgets) : null;
  const [deletingId, setDeletingId] = useState(null);

  const preservedSearch = useMemo(
    () => location.search || window.location.search || "",
    [location.search],
  );

  const onCreate = useCallback(() => {
    navigate({ pathname: "/widgets/new", search: preservedSearch });
  }, [navigate, preservedSearch]);

  useAppTitleBar({
    title: "All widgets",
    primaryAction: { label: "Create widget", onClick: onCreate },
  });

  const goToPlans = useCallback(() => {
    navigate({ pathname: "/plans", search: preservedSearch });
  }, [navigate, preservedSearch]);

  const onEdit = useCallback(
    (widgetId) => {
      if (!widgetId) return;
      navigate({
        pathname: `/widgets/${encodeURIComponent(String(widgetId))}/edit`,
        search: preservedSearch,
      });
    },
    [navigate, preservedSearch],
  );

  const onDelete = useCallback(
    async (widgetId) => {
      if (!widgetId || deletingId) return;
      const name = widgets.find((w) => w.id === widgetId)?.name || "this widget";
      if (!window.confirm(`Delete "${name}"? It will be removed from the storefront.`)) return;
      setDeletingId(widgetId);
      try {
        const result = await deleteWidgetFromDb(widgetId);
        const next = Array.isArray(result.widgets) ? result.widgets : [];
        if (result.metafield_sync_error) {
          window.alert(
            `Widget deleted from database, but storefront sync failed: ${result.metafield_sync_error}`,
          );
        }
        setWidgets(next);
      } catch (e) {
        console.warn("[WidgetsListPage] delete failed", e);
        window.alert(e?.message || "Failed to delete widget");
      } finally {
        setDeletingId(null);
      }
    },
    [widgets, deletingId, setWidgets],
  );

  const showEmpty = !loading && widgets.length === 0;

  return (
    <Page>
      {loading && widgets.length === 0 ? (
        <Card>
          <Box padding="800">
            <InlineStack align="center" blockAlign="center">
              <Spinner accessibilityLabel="Loading widgets" size="large" />
            </InlineStack>
          </Box>
        </Card>
      ) : showEmpty ? (
        <Card>
          <EmptyState
            heading="No widgets yet"
            action={{ content: "Create widget", onAction: onCreate }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              There is no single &quot;Create event&quot; flow — each widget is its own block, and inside it you
              add multiple events (similar to the reference apps).
            </p>
          </EmptyState>
        </Card>
      ) : (
        <BlockStack gap="400">
          {widgets.map((w) => {
            const t = getTemplateById(w.templateId);
            const n = w.events ? w.events.length : 0;
            const locked = Boolean(publishedWidgetId && String(w.id) !== String(publishedWidgetId));
            return (
              <PlanLockOverlay
                key={w.id}
                locked={locked}
                onUnlock={goToPlans}
                label="Upgrade to Pro to edit this widget"
              >
                <Card roundedAbove="sm">
                  <InlineStack align="space-between" blockAlign="start" wrap>
                    <BlockStack gap="200">
                      <Text as="h2" variant="headingSm">
                        {w.name || "Untitled widget"}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Template: {t.label} · {t.mode} view
                      </Text>
                    </BlockStack>
                    <Box>
                      <InlineStack gap="200" blockAlign="center" wrap>
                        <Badge tone="info">{n} event{n === 1 ? "" : "s"}</Badge>
                        <PolarisButton size="slim" onClick={() => onEdit(w.id)}>
                          Edit widget
                        </PolarisButton>
                        <PolarisButton
                          size="slim"
                          tone="critical"
                          loading={deletingId === w.id}
                          onClick={() => onDelete(w.id)}
                        >
                          Delete
                        </PolarisButton>
                      </InlineStack>
                    </Box>
                  </InlineStack>
                </Card>
              </PlanLockOverlay>
            );
          })}
        </BlockStack>
      )}
    </Page>
  );
}
